/* eslint-disable react-refresh/only-export-components --
 * Shared chart primitives: intentionally co-locates palette/style constants with
 * chart components. Fast Refresh granularity does not apply to this module. */
import { motion } from 'motion/react'
import type { ReactNode, CSSProperties } from 'react'
import { TOKENS } from './dashboard-primitives'

/**
 * Warm-editorial chart palette. Sequential warm ramp for device/series colors,
 * plus stable role colors for round-1 / round-2 / positive / warn.
 */
export const CHART_PALETTE = {
  amber: '#e8c5a0',
  amberLight: '#f5d4a8',
  amberDeep: '#caa472',
  terracotta: '#c66e4d',
  terracottaDeep: '#9d4f33',
  ochre: '#b8884a',
  rose: '#a8584e',
  cream: TOKENS.cream,
  /** Sequential ramp for ordered series (oldest → newest, low → high) */
  ramp: ['#caa472', '#e8c5a0', '#f5d4a8', '#b8884a', '#c66e4d', '#9d4f33', '#a8584e', '#7d4a2e'] as const,
  /** Compatible with `categorical` device lists — used by Progress */
  device: ['#e8c5a0', '#caa472', '#c66e4d', '#b8884a', '#a8584e', '#9d4f33', '#f5d4a8', '#7d4a2e'] as const,
  /** Round colors (Sessions) */
  round1: '#e8c5a0',
  round2: '#c66e4d',
}

export const CHART_GRID = {
  stroke: 'rgba(245, 237, 225, 0.08)',
  strokeStrong: 'rgba(245, 237, 225, 0.14)',
}

export const CHART_AXIS_STYLE = {
  tick: { fill: TOKENS.creamDim, fontSize: 11, fontFamily: 'Geist Mono, monospace' } as const,
  line: { stroke: 'rgba(245, 237, 225, 0.18)' } as const,
}

export const WARM_TOOLTIP_STYLE: CSSProperties = {
  backgroundColor: 'rgba(28, 24, 22, 0.95)',
  border: '1px solid rgba(245, 237, 225, 0.12)',
  borderRadius: '12px',
  padding: '10px 12px',
  fontFamily: 'Geist Mono, monospace',
  fontSize: '11px',
  color: TOKENS.cream,
  boxShadow: '0 12px 30px -10px rgba(0,0,0,0.6)',
}

export const WARM_TOOLTIP_ITEM_STYLE: CSSProperties = {
  color: TOKENS.creamSoft,
  fontFamily: 'Geist Mono, monospace',
  fontSize: '11px',
}

export const WARM_TOOLTIP_LABEL_STYLE: CSSProperties = {
  color: TOKENS.creamDim,
  fontFamily: 'Geist Mono, monospace',
  fontSize: '10px',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  marginBottom: 4,
}

/**
 * Editorial chart panel — no card, just a hairline-anchored block with an
 * eyebrow label and optional accessory in the top-right.
 */
export function ChartPanel({
  eyebrow,
  title,
  meta,
  children,
  accessory,
  className = '',
  ariaLabel,
}: {
  eyebrow?: string
  title?: ReactNode
  meta?: ReactNode
  children?: ReactNode
  accessory?: ReactNode
  className?: string
  ariaLabel?: string
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-10% 0px' }}
      transition={{ duration: 0.55 }}
      aria-label={ariaLabel}
      className={`relative ${className}`}
    >
      {(eyebrow || title || accessory || meta) && (
        <div className="flex items-end justify-between gap-4 mb-4">
          <div className="min-w-0">
            {eyebrow && (
              <span
                className="block text-[10px] tracking-[0.28em] uppercase"
                style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
              >
                {eyebrow}
              </span>
            )}
            {title && (
              <h3
                className="mt-2 leading-tight"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 500,
                  fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
                  letterSpacing: '-0.012em',
                  color: TOKENS.cream,
                }}
              >
                {title}
              </h3>
            )}
            {meta && (
              <div
                className="mt-1.5 text-xs leading-relaxed"
                style={{ fontFamily: 'var(--font-body)', color: TOKENS.creamFaint }}
              >
                {meta}
              </div>
            )}
          </div>
          {accessory && <div className="shrink-0">{accessory}</div>}
        </div>
      )}
      {children}
    </motion.section>
  )
}

/**
 * Subtle warm-dark backdrop for chart plotting areas. Restores contrast
 * for amber/terracotta bars and lines against the warm page gradient
 * without turning sections into hard cards. Use to wrap ResponsiveContainer.
 */
export function ChartFrame({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`relative rounded-2xl overflow-hidden ${className}`}
      style={{
        backgroundColor: 'rgba(28, 24, 22, 0.42)',
        boxShadow: 'inset 0 1px 0 rgba(245, 237, 225, 0.06)',
      }}
    >
      {children}
    </div>
  )
}

/**
 * Visually-hidden table for screen-reader-accessible chart data fallback.
 * Use this alongside any chart to satisfy WCAG SC 1.1.1.
 *
 * The `sr-only` class goes on a wrapping `div`, never on the `<table>` itself.
 * `sr-only` works by shrinking the element to 1×1 and clipping it, but CSS
 * table layout treats `width` as a *minimum* — a `display: table` element with
 * `white-space: nowrap` still lays out at its full min-content width (measured
 * at 442px for a four-row chart table). Being `position: absolute`, it then
 * contributes that width to its containing block's scrollable overflow and
 * gives the whole page a horizontal scrollbar on narrow viewports. A plain
 * block wrapper honours the 1px and its `overflow: hidden` stops the table's
 * width from propagating.
 */
export function SrChartTable({
  caption,
  columns,
  rows,
  limit,
}: {
  caption: string
  columns: string[]
  rows: (string | number)[][]
  /**
   * Keep at most this many rows, the most recent ones — every series in this
   * app runs oldest first. Unbounded tables are the trap here: one `<tr>` per
   * session ever, or per calendar week including the silent ones, is fine at
   * forty and thousands of hidden cells after a few years, all of it built on
   * every render for a reader who will never reach the end of it. Pass a limit
   * on anything whose row count grows with the athlete's history. The caption
   * says when it has been applied, so nobody is quietly reading a subset.
   */
  limit?: number
}) {
  const truncated = limit !== undefined && rows.length > limit
  const shown = truncated ? rows.slice(rows.length - limit) : rows
  return (
    <div className="sr-only">
      <table>
        <caption>
          {truncated ? `${caption} (most recent ${limit} of ${rows.length})` : caption}
        </caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((row, idx) => (
            <tr key={idx}>
              {row.map((cell, ci) => (
                <td key={ci}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
